/**
 * DESTRAVE administrativo — chegam aqui admin e técnico responsável
 * (GUPM-261; RT incluído no GUPM-313).
 *
 * Encerra o plantão de um operador que saiu sem passar o turno. É ação de
 * supervisão sobre terceiro: não pede checklist, porque quem destrava não viveu
 * o turno e atestação falsa é pior que nenhuma. É a ausência do checklist que
 * separa, no registro, encerramento atestado de intervenção administrativa.
 *
 * O AUTO-encerramento (o plantonista sai sem rendição) NÃO passa por aqui: mora
 * no formulário de troca, com "— Ninguém —" na lista de entrantes, e leva
 * checklist completo (GUPM-272 Rev.2).
 *
 * Depois do encerramento a operação fica SEM plantonista até alguém abrir o
 * próximo — daí o aviso explícito antes de confirmar. O motivo vai ao audit_log
 * pelo payload do UPDATE; quem encerrou sai derivado (`ended_by`).
 */
import { useState } from 'react';
import { usePopup } from '@/hooks/usePopup';
import { useFecharComConfirmacao } from '@/hooks/useFecharComConfirmacao';
import { ConfirmarDescarte } from '@/components/ConfirmarDescarte';

interface EncerrarPlantaoModalProps {
  isOpen: boolean;
  holderName: string;
  onConfirm: (reason: string, code: string) => void;
  onClose: () => void;
  isPending: boolean;
  error: string | null;
}

export default function EncerrarPlantaoModal({
  isOpen,
  holderName,
  onConfirm,
  onClose,
  isPending,
  error,
}: EncerrarPlantaoModalProps) {
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const canConfirm = reason.trim().length > 0 && /^\d{6}$/.test(code) && !isPending;
  // C-91: ×, Esc, "Voltar" e clique fora com motivo ou código digitado perguntam
  // antes de descartar; enviando, nada fecha.
  const saida = useFecharComConfirmacao({
    preenchido: reason.trim() !== '' || code !== '',
    ocupado: isPending,
    fechar: onClose,
  });
  const popupRef = usePopup<HTMLDivElement>({ aberto: isOpen, aoFechar: saida.pedirFechar, bloqueado: isPending });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) saida.pedirFechar();
      }}
    >
      <div
        ref={popupRef}
        role="dialog"
        aria-modal="true"
        aria-label="Encerrar plantão"
        tabIndex={-1}
        className="w-full max-w-md rounded-xl border-2 border-red-500/50 focus:outline-none bg-jf-surface shadow-[0_0_40px_rgba(239,68,68,0.15)] overflow-hidden">
        <header className="flex items-start justify-between border-b border-jf-border bg-red-500/[0.06] p-4">
          <div>
            <h2 className="text-lg font-bold text-red-300">⏹ Encerrar plantão</h2>
            <p className="text-xs text-jf-muted">
              Encerra o plantão sem iniciar outro — use para destravar um plantão esquecido.
            </p>
          </div>
          <button
            onClick={saida.pedirFechar}
            disabled={isPending}
            className="text-2xl leading-none text-jf-muted hover:text-white disabled:opacity-50"
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <div className="space-y-4 p-4">
          <p className="text-sm text-white">
            Plantão atual de <strong>{holderName}</strong>. Após o encerramento, a operação fica{' '}
            <strong>sem plantonista</strong> até um operador abrir plantão novo.
          </p>

          <div>
            <label
              htmlFor="encerrar-motivo"
              className="block text-[10px] uppercase tracking-wide text-jf-dim mb-1"
            >
              Motivo do encerramento — obrigatório
            </label>
            <textarea
              id="encerrar-motivo"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: operador encerrou o expediente sem passar o plantão…"
              rows={3}
              maxLength={2000}
              className="w-full resize-y rounded-md border border-jf-border bg-jf-surface px-3 py-2 text-sm text-white outline-none focus:border-red-400"
            />
            <p className="mt-1 text-[10px] text-jf-muted">
              O motivo é registrado na auditoria junto com quem encerrou e quando.
            </p>
          </div>

          <div>
            <label
              htmlFor="encerrar-totp"
              className="block text-[10px] uppercase tracking-wide text-jf-dim mb-1"
            >
              Seu código do autenticador — 6 dígitos
            </label>
            <input
              id="encerrar-totp"
              value={code}
              // Só dígitos: colar um código com espaço/hífen não deve travar o
              // confirmar por "formato inválido" sem explicação.
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              className="w-40 rounded-md border border-jf-border bg-jf-surface px-3 py-2 font-mono text-lg tracking-[0.3em] text-white outline-none focus:border-red-400"
            />
            <p className="mt-1 text-[10px] text-jf-muted">
              Microsoft Authenticator, Google Authenticator, Authy ou 1Password.
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300"
            >
              {error}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-3 border-t border-jf-border bg-jf-surface2/40 p-4">
          <button
            onClick={saida.pedirFechar}
            disabled={isPending}
            className="rounded-md border border-jf-border px-4 py-2 text-sm text-jf-muted hover:bg-jf-border disabled:opacity-50"
          >
            Voltar
          </button>
          <button
            onClick={() => onConfirm(reason.trim(), code)}
            disabled={!canConfirm}
            className={`rounded-md px-4 py-2 text-sm font-bold transition ${
              canConfirm
                ? 'bg-red-500 text-white hover:brightness-110'
                : 'cursor-not-allowed bg-jf-surface text-jf-dim border border-jf-border'
            }`}
          >
            {isPending ? 'Encerrando…' : 'Confirmar encerramento'}
          </button>
        </footer>
        {saida.perguntando && <ConfirmarDescarte onContinuar={saida.continuar} onDescartar={saida.descartar} />}
      </div>
    </div>
  );
}
